import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete } from '@mui/icons-material'
import {
    Autocomplete,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import {
    type Colleges,
    type DegreeTypes,
    type EducationEntry,
    type GetCandidateProfileForRecruiterQuery,
    type Majors,
    type MbaColleges,
} from '__generated__/graphql'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type Dispatch,
    type FC,
    type SetStateAction,
} from 'react'
import { GetCandidateEducationOptions } from 'recruiter/graphql/candidates.gql'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateEducationMutation } from 'shared/graphql/candidateProfile.gql'
import { type DropdownOption } from 'shared/models/dropdownOption'
import { SpaceBetweenRow } from 'shared/style/styled'
import {
    formatGraphQLError,
    getPxAsRem,
    MMyyyyToValidDateString,
} from 'shared/utils'
import * as Yup from 'yup'
import { EDUCATION_SECTIONS } from '.'
import { EditButtonContainer, SubtitleContainer } from '../styled'
import EditButtonsContainer from './EditButtonsContainer'
import EducationSectionTitleContainer from './EducationSectionTitleContainer'

/** The types of education from which a recruiter can select. This dictates whether a major (for undergrads) or degree (for grad students) is required. */
const educationTypeOptions = ['Undergraduate', 'Graduate'] as const

/** A convenience type for recruiter-selectable education type options. */
type EducationType = (typeof educationTypeOptions)[number]

export interface CandidateDegreeFormValues
    extends Omit<EducationEntry, 'collegeId'> {
    educationType?: EducationType
    collegeId?: string
}

/**
 * Generates a set of GPA options from 5.0 to 2.0 in increments of 0.1.
 */
const getGpaOptions = () => {
    const gpaOptions: DropdownOption[] = []
    const gpaMaximum = 5.0
    const gpaMinimum = 2.0
    const gpaValueStep = 0.1

    for (let i = gpaMaximum; i >= gpaMinimum; i -= gpaValueStep) {
        gpaOptions.push({
            id: i.toFixed(1),
            value: i.toFixed(1),
        })
    }

    gpaOptions.push({ id: '0.0', value: 'N/A' })

    return gpaOptions
}

const gpaOptions = getGpaOptions()

const gpaScaleOptions: DropdownOption[] = [
    { id: '5.0', value: '5.0' },
    { id: '4.3', value: '4.3' },
    { id: '4.0', value: '4.0' },
]

interface DegreesProps {
    education: NonNullable<
        NonNullable<
            GetCandidateProfileForRecruiterQuery['candidate']
        >['education']
    >
    edit: EDUCATION_SECTIONS | null
    setEdit: Dispatch<SetStateAction<EDUCATION_SECTIONS | null>>
    setEditWithUnsavedChanges: (newEdit: EDUCATION_SECTIONS | null) => void
    candidateUserId: string
    closeAfterSave: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
}

export const Degrees: FC<DegreesProps> = ({
    education = [],
    edit,
    setEdit,
    candidateUserId,
    closeAfterSave,
    setUnsavedChangesExist,
    setEditWithUnsavedChanges,
}) => {
    const toast = useToast()
    const [undergradDegreeOptions, setUndergradDegreeOptions] = useState<
        DropdownOption<Majors>[]
    >([])
    const [graduateDegreeOptions, setGraduateDegreeOptions] = useState<
        DropdownOption<DegreeTypes>[]
    >([])
    const [allDegreeOptions, setAllDegreeOptions] = useState<
        DropdownOption<Majors | DegreeTypes>[]
    >([])
    const [collegeOptions, setCollegeOptions] = useState<
        DropdownOption<Colleges>[]
    >([])
    const [mbaCollegeOptions, setMbaCollegeOptions] = useState<
        DropdownOption<MbaColleges>[]
    >([])
    const [allCollegeOptions, setAllCollegeOptions] = useState<
        DropdownOption<Colleges | MbaColleges>[]
    >([])

    useQuery(GetCandidateEducationOptions, {
        onCompleted: (data) => {
            setUndergradDegreeOptions(data.majors)
            setGraduateDegreeOptions(data.degreeTypes)
            setAllDegreeOptions([...data.majors, ...data.degreeTypes])
            setCollegeOptions(data.colleges)
            setMbaCollegeOptions(data.mbaColleges)
            setAllCollegeOptions([...data.colleges, ...data.mbaColleges])
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate education options'
                ),
            })
        },
    })

    const [updateCandidateEducation] = useMutation(
        UpdateCandidateEducationMutation,
        {
            onCompleted: (data) => {
                if (data.updateCandidateEducation.success) {
                    toast({
                        severity: 'success',
                        title: 'Success!',
                        message: 'Education has been updated',
                    })
                    closeAfterSave()
                } else {
                    toast({
                        severity: 'error',
                        title: 'Error',
                        message: `Failed to update education: ${data.updateCandidateEducation.message}`,
                    })
                }
            },
            onError: (error) => {
                console.error(error)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: formatGraphQLError(
                        error,
                        'Unable to update education.'
                    ),
                })
            },
        }
    )

    const validationSchema: Yup.ArraySchema<
        CandidateDegreeFormValues[],
        Yup.AnyObject,
        unknown
    > = Yup.array()
        .of(
            Yup.object({
                id: Yup.string(),
                educationType: Yup.string<EducationType>(),
                collegeId: Yup.string().required('College is required'),
                degreeIds: Yup.array()
                    .of(Yup.string().required('Major/Degree is required'))
                    .required('Major/Degree is required'),
                graduationDate: Yup.string(),
                gpa: Yup.number()
                    .min(0)
                    .max(5)
                    .max(Yup.ref('gpaScale'), 'GPA cannot exceed GPA scale'),
                gpaScale: Yup.number(),
            })
        )
        .required()

    const getEducationType = useCallback(
        (degreeIds: CandidateDegreeFormValues['degreeIds']): EducationType => {
            if (!degreeIds) return 'Undergraduate'

            if (
                graduateDegreeOptions.some(({ id }) => degreeIds?.includes(id))
            ) {
                return 'Graduate'
            }

            if (
                undergradDegreeOptions.some(({ id }) => degreeIds.includes(id))
            ) {
                return 'Undergraduate'
            }

            return 'Undergraduate'
        },
        [graduateDegreeOptions, undergradDegreeOptions]
    )

    const getDegreesValue = (
        degreeIds: CandidateDegreeFormValues['degreeIds']
    ) => {
        const educationType = getEducationType(degreeIds)
        const isUndergraduate = educationType === 'Undergraduate'
        const degreeOptions = isUndergraduate
            ? undergradDegreeOptions
            : graduateDegreeOptions
        const values = degreeOptions.filter(({ id }) => degreeIds?.includes(id))

        return values
    }

    const { resetForm, ...formik } = useFormik<CandidateDegreeFormValues[]>({
        initialValues: education.map((entry) => ({
            id: entry.id,
            educationType: getEducationType(
                entry?.degrees?.map(({ id }) => id)
            ),
            collegeId: entry.college?.id || '',
            degreeIds:
                getEducationType(entry?.degrees?.map(({ id }) => id)) ===
                'Undergraduate'
                    ? entry.majors?.map(({ major }) => major?.id || '') || []
                    : entry?.degrees?.map(({ id }) => id),
            graduationDate: entry.graduationDate ?? undefined,
            gpa: entry.gpa ?? undefined,
            gpaScale: entry.gpaScale ?? undefined,
        })),
        validationSchema,
        onSubmit: (degreeEntries) => {
            handleUpdateCandidateEducation(degreeEntries)
        },
        enableReinitialize: true,
    })

    const handleUpdateCandidateEducation = (
        degreeEntries: CandidateDegreeFormValues[]
    ) => {
        const educationEntries = degreeEntries.map((degreeEntry) => ({
            id: degreeEntry.id,
            degreeIds: degreeEntry.degreeIds ?? null,
            collegeId: degreeEntry.collegeId ?? '',
            graduationDate: degreeEntry.graduationDate ?? null,
            gpa: degreeEntry.gpa ?? null,
            gpaScale: degreeEntry.gpaScale ?? null,
        }))
        const hasValidEntries = educationEntries.every(
            (entry) => entry.collegeId !== ''
        )

        if (hasValidEntries) {
            updateCandidateEducation({
                variables: {
                    userId: candidateUserId,
                    educationEntries,
                },
            })
        }
    }

    const handleAddEducationEntry = () => {
        if (edit !== EDUCATION_SECTIONS.DEGREES)
            setEditWithUnsavedChanges(EDUCATION_SECTIONS.DEGREES)

        formik.setValues((values) => [
            ...values,
            {
                educationType: 'Undergraduate',
                collegeId: '',
                degreeIds: [],
                graduationDate: undefined,
                gpa: undefined,
                gpaScale: undefined,
            },
        ])
    }

    const handleRemoveEducationEntry = (index) => {
        if (edit !== EDUCATION_SECTIONS.DEGREES)
            setEditWithUnsavedChanges(EDUCATION_SECTIONS.DEGREES)

        formik.setValues((values) => {
            const updatedEntries = structuredClone(values)
            updatedEntries.splice(index, 1)
            return updatedEntries
        })
    }

    // Let the parent component know if there are unsaved changes to help drive
    // the confirmation dialog that appears when switching between editable
    // sections.
    useEffect(() => {
        setUnsavedChangesExist(
            formik.dirty && edit === EDUCATION_SECTIONS.DEGREES
        )
    }, [formik.dirty, edit, setUnsavedChangesExist])

    // Reset the form when the user agrees to switch to a different editable
    // section via the confirmation dialog.
    useEffect(() => {
        if (edit !== EDUCATION_SECTIONS.DEGREES) resetForm()
    }, [edit, resetForm])

    // Determine the ID of the MBA degree type to help with filtering the
    // college options shown to the user.
    const mbaDegreeId = useMemo(
        () => graduateDegreeOptions.find((major) => major.value === 'MBA')?.id,
        [graduateDegreeOptions]
    )

    return (
        <>
            <SpaceBetweenRow sx={{ marginBottom: getPxAsRem(32) }}>
                <Typography variant="h4">Education</Typography>
                <Button variant="outlined" onClick={handleAddEducationEntry}>
                    Add Education
                </Button>
            </SpaceBetweenRow>

            <form onSubmit={formik.handleSubmit}>
                <EducationSectionTitleContainer
                    setEditWithUnsavedChanges={setEditWithUnsavedChanges}
                    edit={edit}
                    sectionType={EDUCATION_SECTIONS.DEGREES}
                    title={'Degrees'}
                    titleId="degreesLabel"
                />

                {formik.values.map(
                    (
                        {
                            id,
                            educationType,
                            degreeIds,
                            collegeId,
                            graduationDate,
                            gpa,
                            gpaScale,
                        },
                        index
                    ) => (
                        <Grid
                            key={id || index}
                            container
                            rowSpacing={3}
                            columnSpacing={2}
                            sx={{ marginBottom: getPxAsRem(24) }}
                        >
                            <SubtitleContainer item xs={12}>
                                <Typography variant="subtitle2">
                                    Degree {index + 1}
                                </Typography>
                                {formik.values.length > 1 && (
                                    <IconButton
                                        onClick={() =>
                                            handleRemoveEducationEntry(index)
                                        }
                                        aria-label="remove degree"
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </SubtitleContainer>

                            {/* Education Type */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <Autocomplete
                                        disableClearable
                                        fullWidth
                                        options={educationTypeOptions}
                                        value={educationType}
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue(
                                                `${index}.degreeIds`,
                                                undefined
                                            )

                                            formik.setFieldValue(
                                                `${index}.collegeId`,
                                                undefined
                                            )

                                            formik.setFieldValue(
                                                `${index}.educationType`,
                                                newValue
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Education Type"
                                                helperText={
                                                    formik.touched[index]
                                                        ?.educationType &&
                                                    formik.errors[index]
                                                        ?.educationType
                                                }
                                                error={
                                                    formik.touched[index]
                                                        ?.educationType &&
                                                    Boolean(
                                                        formik.errors[index]
                                                            ?.educationType
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                ) : (
                                    <DisplayField
                                        label="Education Type"
                                        value={educationType || '-'}
                                    />
                                )}
                            </Grid>

                            {/* Degree (aka Major for undergrads) */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <Autocomplete
                                        fullWidth
                                        multiple={true}
                                        options={
                                            educationType === 'Undergraduate'
                                                ? undergradDegreeOptions
                                                : graduateDegreeOptions
                                        }
                                        getOptionLabel={({ value }) => value}
                                        value={getDegreesValue(degreeIds)}
                                        getOptionDisabled={(option) =>
                                            educationType === 'Graduate' &&
                                            (degreeIds?.length || 0) >= 1
                                        }
                                        onChange={(event, newValue) => {
                                            const isMbaBeforeChange =
                                                formik.values[
                                                    `${index}`
                                                ].degreeIds?.includes(
                                                    mbaDegreeId ?? ''
                                                )
                                            // Clears out if MBA was the selected and now it's removed, if it was changed from MBA to a different degree, or if MBA was selected
                                            const shouldClearCollegeID =
                                                (isMbaBeforeChange &&
                                                    newValue?.length === 0) ||
                                                (isMbaBeforeChange &&
                                                    newValue[0]?.id !==
                                                        mbaDegreeId) ||
                                                (!isMbaBeforeChange &&
                                                    newValue[0]?.id ===
                                                        mbaDegreeId)

                                            if (shouldClearCollegeID) {
                                                formik.setFieldValue(
                                                    `${index}.collegeId`,
                                                    undefined
                                                )
                                            }

                                            formik.setFieldValue(
                                                `${index}.degreeIds`,
                                                newValue.map((i) => i.id)
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={
                                                    educationType ===
                                                    'Undergraduate'
                                                        ? 'Major'
                                                        : 'Degree'
                                                }
                                                helperText={
                                                    formik.touched[index]
                                                        ?.degreeIds &&
                                                    formik.errors[index]
                                                        ?.degreeIds
                                                }
                                                error={
                                                    formik.touched[index]
                                                        ?.degreeIds &&
                                                    Boolean(
                                                        formik.errors[index]
                                                            ?.degreeIds
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                ) : (
                                    <DisplayField
                                        label={
                                            educationType === 'Undergraduate'
                                                ? 'Major'
                                                : 'Degree'
                                        }
                                        value={
                                            allDegreeOptions
                                                .filter((i) =>
                                                    degreeIds?.includes(i.id)
                                                )
                                                .map((i) => i.value)
                                                .join(', ') || '-'
                                        }
                                    />
                                )}
                            </Grid>

                            {/* College/University */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <Autocomplete
                                        fullWidth
                                        options={
                                            degreeIds?.includes(
                                                mbaDegreeId || ''
                                            )
                                                ? mbaCollegeOptions
                                                : collegeOptions
                                        }
                                        getOptionLabel={({ value }) => value}
                                        value={
                                            allCollegeOptions.find(
                                                ({ id }) => id === collegeId
                                            ) ?? null
                                        }
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue(
                                                `${index}.collegeId`,
                                                newValue?.id ?? ''
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="College/University"
                                                required={Boolean(
                                                    formik.values[index]
                                                        .collegeId
                                                )}
                                                variant="outlined"
                                                helperText={
                                                    formik.touched[index]
                                                        ?.collegeId &&
                                                    formik.errors[index]
                                                        ?.collegeId
                                                }
                                                error={
                                                    formik.touched[index]
                                                        ?.collegeId &&
                                                    Boolean(
                                                        formik.errors[index]
                                                            ?.collegeId
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                ) : (
                                    <DisplayField
                                        label="College/University"
                                        value={
                                            allCollegeOptions.find(
                                                ({ id }) => id === collegeId
                                            )?.value || '-'
                                        }
                                    />
                                )}
                            </Grid>

                            {/* Graduation Date */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <FormControl fullWidth>
                                        <DatePicker
                                            label="Graduation Date"
                                            format="MM/yyyy"
                                            value={
                                                graduationDate
                                                    ? new Date(
                                                          MMyyyyToValidDateString(
                                                              graduationDate
                                                          )
                                                      )
                                                    : null
                                            }
                                            views={['year', 'month']}
                                            onChange={(date: Date | null) => {
                                                formik.setFieldValue(
                                                    `${index}.graduationDate`,
                                                    date
                                                        ? format(
                                                              date,
                                                              'MM-yyyy'
                                                          )
                                                        : ''
                                                )
                                            }}
                                            slotProps={{
                                                textField: {
                                                    helperText:
                                                        formik.touched[index]
                                                            ?.graduationDate &&
                                                        formik.errors[index]
                                                            ?.graduationDate,
                                                    error:
                                                        formik.touched[index]
                                                            ?.graduationDate &&
                                                        Boolean(
                                                            formik.errors[index]
                                                                ?.graduationDate
                                                        ),
                                                },
                                            }}
                                        />
                                    </FormControl>
                                ) : (
                                    <DisplayField
                                        label="Graduation Date"
                                        value={
                                            graduationDate
                                                ? format(
                                                      new Date(
                                                          MMyyyyToValidDateString(
                                                              graduationDate
                                                          )
                                                      ),
                                                      'MM/yyyy'
                                                  )
                                                : '-'
                                        }
                                    />
                                )}
                            </Grid>

                            {/* Cumulative GPA */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <Autocomplete
                                        fullWidth
                                        options={gpaOptions}
                                        getOptionLabel={({ value }) => value}
                                        value={
                                            gpaOptions.find(
                                                ({ id }) =>
                                                    id === gpa?.toFixed(1)
                                            ) ?? null
                                        }
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue(
                                                `${index}.gpa`,
                                                newValue?.id
                                                    ? parseFloat(newValue.id)
                                                    : null
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cumulative GPA"
                                                error={
                                                    formik.touched[index]
                                                        ?.gpa &&
                                                    Boolean(
                                                        formik.errors[index]
                                                            ?.gpa
                                                    )
                                                }
                                                helperText={
                                                    formik.touched[index]
                                                        ?.gpa &&
                                                    formik.errors[index]?.gpa
                                                }
                                            />
                                        )}
                                    />
                                ) : (
                                    <DisplayField
                                        label="Cumulative GPA"
                                        value={
                                            gpaOptions.find(
                                                ({ id }) =>
                                                    id === gpa?.toFixed(1)
                                            )?.value || '-'
                                        }
                                    />
                                )}
                            </Grid>

                            {/* GPA Scale */}
                            <Grid item xs={12} md={6}>
                                {edit === EDUCATION_SECTIONS.DEGREES ? (
                                    <Autocomplete
                                        fullWidth
                                        options={gpaScaleOptions}
                                        getOptionLabel={({ value }) => value}
                                        value={
                                            gpaScaleOptions.find(
                                                ({ id }) =>
                                                    id === gpaScale?.toFixed(1)
                                            ) ?? null
                                        }
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue(
                                                `${index}.gpaScale`,
                                                newValue?.id
                                                    ? parseFloat(newValue.id)
                                                    : null
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="GPA Scale"
                                                error={
                                                    formik.touched[index]
                                                        ?.gpaScale &&
                                                    Boolean(
                                                        formik.errors[index]
                                                            ?.gpaScale
                                                    )
                                                }
                                                helperText={
                                                    formik.touched[index]
                                                        ?.gpaScale &&
                                                    formik.errors[index]
                                                        ?.gpaScale
                                                }
                                            />
                                        )}
                                    />
                                ) : (
                                    <DisplayField
                                        label="GPA Scale"
                                        value={
                                            gpaOptions.find(
                                                ({ id }) =>
                                                    id === gpaScale?.toFixed(1)
                                            )?.value || '-'
                                        }
                                    />
                                )}
                            </Grid>
                        </Grid>
                    )
                )}

                <EditButtonContainer item xs={12} md={6}>
                    <Button
                        onClick={handleAddEducationEntry}
                        variant="text"
                        startIcon={<AddCircle />}
                    >
                        Add Degree
                    </Button>
                </EditButtonContainer>

                <EditButtonsContainer
                    activeEditingSection={edit}
                    sectionType={EDUCATION_SECTIONS.DEGREES}
                    setEdit={setEdit}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                    unsavedChangesExist={formik.dirty}
                    onCancel={() => {
                        resetForm()
                    }}
                />
            </form>
        </>
    )
}

function DisplayField({ label, value }: { label: string; value?: string }) {
    return (
        <>
            <InputLabel sx={(theme) => ({ fontSize: theme.fontSize.xsmall })}>
                {label}
            </InputLabel>

            <Typography variant="body1">{value || '-'}</Typography>
        </>
    )
}
